import * as React from 'react';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type {
  DocumentLineInput,
  DocumentLineSummary,
  PaymentApplicationSummary,
} from '@bits/shared/zod';
import { formatInr, paise, rupeesToPaise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors, type SortState } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const billingDocumentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/documents/$documentId',
  component: BillingDocumentDetailPage,
});

type LineSortKey =
  | 'line'
  | 'description'
  | 'hsn'
  | 'qty'
  | 'rate'
  | 'taxable'
  | 'cgst'
  | 'sgst'
  | 'igst';
const EMPTY_LINES: DocumentLineSummary[] = [];
const LINES_INITIAL_SORT: SortState<LineSortKey> = { key: 'line', direction: 'asc' };
const lineSortAccessors: SortAccessors<DocumentLineSummary, LineSortKey> = {
  line: (l) => l.lineNo,
  description: (l) => l.description,
  hsn: (l) => l.hsnSac,
  qty: (l) => l.qty,
  rate: (l) => l.ratePaise,
  taxable: (l) => l.taxablePaise,
  cgst: (l) => l.cgstPaise,
  sgst: (l) => l.sgstPaise,
  igst: (l) => l.igstPaise,
};

type PaymentSortKey = 'appliedAt' | 'transaction' | 'amount';
const EMPTY_PAYMENTS: PaymentApplicationSummary[] = [];
const paymentSortAccessors: SortAccessors<PaymentApplicationSummary, PaymentSortKey> = {
  appliedAt: (p) => p.createdAt,
  transaction: (p) => p.transactionId,
  amount: (p) => p.amountPaise,
};

function BillingDocumentDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { documentId } = billingDocumentDetailRoute.useParams();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [payDialogOpen, setPayDialogOpen] = React.useState(false);
  const [writeOffDialogOpen, setWriteOffDialogOpen] = React.useState(false);
  const [cnDialogOpen, setCnDialogOpen] = React.useState(false);

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), retry: false });
  const isAdmin = React.useMemo(
    () => (meQuery.data?.person.roles ?? []).includes('Admin'),
    [meQuery.data],
  );

  const detailQuery = useQuery({
    queryKey: ['document', documentId],
    queryFn: () => api.getDocument(documentId),
  });
  const doc = detailQuery.data;
  const { rows: lines, ...lineSorter } = useSortedRows(
    doc?.lines ?? EMPTY_LINES,
    lineSortAccessors,
    LINES_INITIAL_SORT,
  );
  const { rows: payments, ...paymentSorter } = useSortedRows(
    doc?.payments ?? EMPTY_PAYMENTS,
    paymentSortAccessors,
  );
  const clientQuery = useQuery({
    queryKey: ['client', doc?.clientId ?? ''],
    queryFn: () => api.getClient(doc?.clientId ?? ''),
    enabled: !!doc,
  });
  const projectsQuery = useQuery({
    queryKey: ['projects-for-billing'],
    queryFn: () => api.listProjects({}),
    enabled: !!doc?.projectId,
  });
  const projectLabel = React.useMemo(() => {
    if (!doc?.projectId) return 'None';
    const p = (projectsQuery.data ?? []).find((x) => x.id === doc.projectId);
    return p ? `${p.code} · ${p.name}` : doc.projectId;
  }, [doc?.projectId, projectsQuery.data]);

  const txnQuery = useQuery({
    queryKey: ['income-txns-for-doc', doc?.clientId ?? ''],
    queryFn: () =>
      api.listTransactions({
        pageSize: 100,
      }),
    enabled: !!doc && (doc.status === 'issued' || doc.status === 'partially_paid'),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    await queryClient.invalidateQueries({ queryKey: ['documents'] });
  };

  const issueMutation = useMutation({
    mutationFn: () => api.issueDocument(documentId),
    onSuccess: async () => {
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const payForm = useForm<{ transactionId: string; amountRupees: number }>({
    defaultValues: { transactionId: '', amountRupees: 0 },
  });
  const payMutation = useMutation({
    mutationFn: (v: { transactionId: string; amountRupees: number }) =>
      api.applyPayment(documentId, {
        transactionId: v.transactionId,
        amountPaise: Number(rupeesToPaise(v.amountRupees)),
      }),
    onSuccess: async () => {
      setPayDialogOpen(false);
      payForm.reset({ transactionId: '', amountRupees: 0 });
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const writeOffForm = useForm<{ reason: string }>({ defaultValues: { reason: '' } });
  const writeOffMutation = useMutation({
    mutationFn: (v: { reason: string }) => api.writeOffDocument(documentId, { reason: v.reason }),
    onSuccess: async () => {
      setWriteOffDialogOpen(false);
      writeOffForm.reset({ reason: '' });
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const cnForm = useForm<{
    issueDate: string;
    reason: string;
    description: string;
    hsnSac: string;
    qtyDecimal: number;
    rateRupees: number;
    gstRateBpp: number;
  }>({
    defaultValues: {
      issueDate: todayIso(),
      reason: '',
      description: 'Reversal against invoice',
      hsnSac: '',
      qtyDecimal: 1,
      rateRupees: 0,
      gstRateBpp: 1800,
    },
  });
  const cnMutation = useMutation({
    mutationFn: (v: {
      issueDate: string;
      reason: string;
      description: string;
      hsnSac: string;
      qtyDecimal: number;
      rateRupees: number;
      gstRateBpp: number;
    }) => {
      const line: DocumentLineInput = {
        description: v.description,
        hsnSac: v.hsnSac || undefined,
        qty: Math.round(v.qtyDecimal * 1000),
        ratePaise: Number(rupeesToPaise(v.rateRupees)),
        gstRateBpp: v.gstRateBpp,
      } as DocumentLineInput;
      return api.createCreditNote({
        refDocumentId: documentId,
        issueDate: v.issueDate,
        reason: v.reason,
        lines: [line],
      });
    },
    onSuccess: async (res) => {
      setCnDialogOpen(false);
      setErrorMessage(null);
      await invalidate();
      await navigate({
        to: '/billing/documents/$documentId',
        params: { documentId: res.documentId },
      });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const downloadPdf = async () => {
    try {
      const blob = await api.downloadDocumentPdf(documentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${doc?.number ?? documentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(toMessage(err));
    }
  };

  if (detailQuery.isPending) {
    return (
      <AppShell>
        <div className="text-sm">Loading…</div>
      </AppShell>
    );
  }
  if (!doc) {
    return (
      <AppShell>
        <div className="text-sm text-red-700">Document not found.</div>
      </AppShell>
    );
  }

  const canIssue = doc.status === 'draft';
  const canPay = doc.status === 'issued' || doc.status === 'partially_paid';
  const canWriteOff = isAdmin && (doc.status === 'issued' || doc.status === 'partially_paid');
  const canCreditNote =
    (doc.docType === 'tax_invoice' || doc.docType === 'bill_of_supply') &&
    (doc.status === 'issued' || doc.status === 'partially_paid' || doc.status === 'paid');

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {doc.docType} · {doc.number ?? doc.requestedNumber ?? 'draft'}
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Issued {doc.issuedAt ? new Date(doc.issuedAt).toLocaleDateString() : '—'} ·
            <Badge variant={badgeVariant(doc.status)} className="ml-2">
              {doc.status}
            </Badge>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canIssue ? (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  void navigate({
                    to: '/billing/documents/$documentId/edit',
                    params: { documentId },
                  })
                }
              >
                Edit draft
              </Button>
              <Button onClick={() => issueMutation.mutate()} disabled={issueMutation.isPending}>
                {issueMutation.isPending ? 'Issuing…' : 'Issue'}
              </Button>
            </>
          ) : null}
          {doc.pdfAttachmentId ? (
            <Button variant="outline" onClick={downloadPdf}>
              Download PDF
            </Button>
          ) : null}
          {canPay ? (
            <Button variant="outline" onClick={() => setPayDialogOpen(true)}>
              Apply payment
            </Button>
          ) : null}
          {canCreditNote ? (
            <Button variant="outline" onClick={() => setCnDialogOpen(true)}>
              Create credit note
            </Button>
          ) : null}
          {canWriteOff ? (
            <Button variant="destructive" onClick={() => setWriteOffDialogOpen(true)}>
              Write off
            </Button>
          ) : null}
        </div>
      </div>

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Header</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div>
              <strong>Issue:</strong> {doc.issueDate}
            </div>
            <div>
              <strong>Due:</strong> {doc.dueDate ?? '—'}
            </div>
            <div>
              <strong>Place of supply:</strong> {doc.placeOfSupplyState}
            </div>
            <div>
              <strong>Client:</strong> {clientQuery.data?.displayName ?? doc.clientId}
            </div>
            <div>
              <strong>Project:</strong> {projectLabel}
            </div>
            {doc.customerNotes ? (
              <div>
                <strong>Notes:</strong> {doc.customerNotes}
              </div>
            ) : null}
            {doc.termsText ? (
              <div className="whitespace-pre-line">
                <strong>Terms:</strong> {doc.termsText}
              </div>
            ) : null}
            {doc.gstReasonCode ? (
              <div>
                <strong>GST reason:</strong> {doc.gstReasonCode}
              </div>
            ) : null}
            {doc.refDocumentId ? (
              <div>
                <strong>Ref doc:</strong> {doc.refDocumentId}
              </div>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Totals</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div>
              <strong>Subtotal:</strong> {formatInr(paise(doc.subtotalPaise))}
            </div>
            <div>
              <strong>Tax:</strong> {formatInr(paise(doc.taxPaise))}
            </div>
            <div>
              <strong>Total:</strong> {formatInr(paise(doc.totalPaise))}
            </div>
            <div className="mt-2 text-base">
              <strong>Amount due:</strong> {formatInr(paise(doc.amountDuePaise))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Identity snapshot</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {doc.identitySnapshot ? (
              <>
                <div>
                  <strong>{doc.identitySnapshot.legalName}</strong>
                </div>
                {doc.identitySnapshot.gstin ? <div>GSTIN: {doc.identitySnapshot.gstin}</div> : null}
                {doc.identitySnapshot.pan ? <div>PAN: {doc.identitySnapshot.pan}</div> : null}
                {doc.identitySnapshot.udyamNo ? (
                  <div>Udyam: {doc.identitySnapshot.udyamNo}</div>
                ) : null}
                <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                  Frozen at issue-time.
                </div>
              </>
            ) : (
              <div className="text-[hsl(var(--muted-foreground))]">Not yet issued.</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lines</CardTitle>
          <CardDescription>
            {doc.lines.length} line{doc.lines.length === 1 ? '' : 's'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-[hsl(var(--muted-foreground))]">
                <tr className="text-left">
                  <SortableTh sortKey="line" controller={lineSorter} className="py-2 pr-3">
                    #
                  </SortableTh>
                  <SortableTh sortKey="description" controller={lineSorter} className="py-2 pr-3">
                    Description
                  </SortableTh>
                  <SortableTh sortKey="hsn" controller={lineSorter} className="py-2 pr-3">
                    HSN
                  </SortableTh>
                  <SortableTh sortKey="qty" controller={lineSorter} align="right" className="py-2 pr-3">
                    Qty
                  </SortableTh>
                  <SortableTh sortKey="rate" controller={lineSorter} align="right" className="py-2 pr-3">
                    Rate
                  </SortableTh>
                  <SortableTh sortKey="taxable" controller={lineSorter} align="right" className="py-2 pr-3">
                    Taxable
                  </SortableTh>
                  <SortableTh sortKey="cgst" controller={lineSorter} align="right" className="py-2 pr-3">
                    CGST
                  </SortableTh>
                  <SortableTh sortKey="sgst" controller={lineSorter} align="right" className="py-2 pr-3">
                    SGST
                  </SortableTh>
                  <SortableTh sortKey="igst" controller={lineSorter} align="right" className="py-2 pr-3">
                    IGST
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} className="border-t border-[hsl(var(--border))]">
                    <td className="py-2 pr-3">{l.lineNo}</td>
                    <td className="py-2 pr-3">{l.description}</td>
                    <td className="py-2 pr-3">{l.hsnSac ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{(l.qty / 1000).toFixed(3)}</td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatInr(paise(l.ratePaise))}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatInr(paise(l.taxablePaise))}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatInr(paise(l.cgstPaise))}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatInr(paise(l.sgstPaise))}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatInr(paise(l.igstPaise))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payments</CardTitle>
        </CardHeader>
        <CardContent>
          {doc.payments.length === 0 ? (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">No payments applied.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-[hsl(var(--muted-foreground))]">
                <tr className="text-left">
                  <SortableTh sortKey="appliedAt" controller={paymentSorter} className="py-2 pr-3">
                    Applied at
                  </SortableTh>
                  <SortableTh sortKey="transaction" controller={paymentSorter} className="py-2 pr-3">
                    Transaction
                  </SortableTh>
                  <SortableTh sortKey="amount" controller={paymentSorter} align="right" className="py-2 pr-3">
                    Amount
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-[hsl(var(--border))]">
                    <td className="py-2 pr-3">{new Date(p.createdAt).toLocaleString()}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{p.transactionId}</td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {formatInr(paise(p.amountPaise))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pay dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply payment</DialogTitle>
            <DialogDescription>Attach a ledger transaction to this document.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={payForm.handleSubmit((v) => payMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <Field
              label="Transaction"
              required
              error={payForm.formState.errors.transactionId?.message}
              hint={
                (txnQuery.data?.items ?? []).length === 0
                  ? 'No transactions yet — record the receipt in Ledger → Transactions first.'
                  : undefined
              }
            >
              <Select {...payForm.register('transactionId', { required: 'Pick a transaction' })}>
                <option value="">Select…</option>
                {(txnQuery.data?.items ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.businessDate} · {t.direction} · {formatInr(paise(t.amountPaise))} ·{' '}
                    {t.description}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Amount to apply (INR)"
              required
              error={payForm.formState.errors.amountRupees?.message}
            >
              <Input
                type="number"
                step="0.01"
                {...payForm.register('amountRupees', {
                  valueAsNumber: true,
                  required: 'Enter an amount',
                  min: { value: 0.01, message: 'Amount must be positive' },
                })}
              />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={payMutation.isPending}>
                {payMutation.isPending ? 'Applying…' : 'Apply'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Write-off dialog */}
      <Dialog open={writeOffDialogOpen} onOpenChange={setWriteOffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Write off document</DialogTitle>
            <DialogDescription>Admin-only. Records a reason and locks the doc.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={writeOffForm.handleSubmit((v) => writeOffMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <div className="flex flex-col gap-1.5">
              <Label>Reason</Label>
              <Input {...writeOffForm.register('reason', { required: true })} />
            </div>
            <DialogFooter>
              <Button type="submit" variant="destructive" disabled={writeOffMutation.isPending}>
                {writeOffMutation.isPending ? 'Writing off…' : 'Write off'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credit note dialog */}
      <Dialog open={cnDialogOpen} onOpenChange={setCnDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create credit note draft</DialogTitle>
            <DialogDescription>
              References this document. Draft is created; you'll issue it separately.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={cnForm.handleSubmit((v) => cnMutation.mutate(v))}
            className="flex flex-col gap-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Issue date</Label>
                <Input type="date" {...cnForm.register('issueDate', { required: true })} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Reason</Label>
                <Input {...cnForm.register('reason', { required: true })} />
              </div>
              <div className="col-span-2 flex flex-col gap-1.5">
                <Label>Description</Label>
                <Input {...cnForm.register('description', { required: true })} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>HSN/SAC</Label>
                <Input {...cnForm.register('hsnSac')} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Qty</Label>
                <Input
                  type="number"
                  step="0.001"
                  {...cnForm.register('qtyDecimal', { valueAsNumber: true })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>Rate (INR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  {...cnForm.register('rateRupees', { valueAsNumber: true })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>GST rate</Label>
                <Select {...cnForm.register('gstRateBpp', { valueAsNumber: true })}>
                  <option value={0}>0%</option>
                  <option value={500}>5%</option>
                  <option value={1200}>12%</option>
                  <option value={1800}>18%</option>
                  <option value={2800}>28%</option>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={cnMutation.isPending}>
                {cnMutation.isPending ? 'Creating…' : 'Create draft'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function todayIso(): string {
  const d = new Date();
  const off = (5 * 60 + 30) * 60 * 1000;
  return new Date(d.getTime() + off).toISOString().slice(0, 10);
}

function badgeVariant(status: string): 'default' | 'muted' | 'destructive' {
  if (status === 'paid') return 'default';
  if (status === 'written_off') return 'destructive';
  return 'muted';
}
